import { INavItem, INavSection } from '@adaptiva/core/app-shell';
import { alphabetize, FileGenerator, ServerLogsService } from '@adaptiva/core/common';
import { MenuItem } from '@adaptiva/core/menus';
import { INotificationService } from '@adaptiva/core/notifications';
import { OverlayService } from '@adaptiva/core/overlays';
import { SnackbarService } from '@adaptiva/core/snackbars';
import { AuthenticationService, EJavaClassNames, GlobalPermissions, GlobalUiPermissionsService, ObjectSecurityManagerService, SystemConfigService } from '@adaptiva/data-service';
import { SideNavMenuController } from '@adaptiva/widgets/app-shell';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, of, take } from 'rxjs';
import { SETTINGS_NAVIGATION } from '../../../constants/navigation';
import { ClientLogsService } from '../../../services';
import { ClientLogOverlayComponent } from '../../actions';

@Injectable()
export class AdminPortalHeaderMenuController extends SideNavMenuController {

	protected readonly auth: AuthenticationService = inject(AuthenticationService);

	protected readonly systemConfig: SystemConfigService = inject(SystemConfigService);

	protected readonly globalUiPermissions: GlobalUiPermissionsService = inject(GlobalUiPermissionsService);

	protected readonly objectSecurity: ObjectSecurityManagerService = inject(ObjectSecurityManagerService);

	protected readonly overlayService: OverlayService = inject(OverlayService);

	protected readonly snackbarService: SnackbarService = inject(SnackbarService);

	protected readonly notifications: INotificationService = inject(INotificationService);

	protected readonly serverLogsService: ServerLogsService = inject(ServerLogsService);

	protected readonly clientLogsService: ClientLogsService = inject(ClientLogsService);

	public override generate(_navItem: INavItem): Observable<MenuItem<INavItem>[]> {
		return of([
			...alphabetize([
				this.createLogsMenuItem(),
				this.createLicensingMenuItem(),
				this.createSettingsMenuItem(),
				this.createThemesMenuItem(),
				this.createImportMenuItem(),
			], 'name'),
		]);
	}

	protected createLogsMenuItem(): MenuItem<INavItem> {
		const _item: MenuItem<INavItem> = this.createItem({
			id: 'logs',
			name: 'Logs',
			children: [
				this.createDownloadAllServerLogsMenuItem(),
				this.createDownloadServerErrorLogsMenuItem(),
				this.createClearWebLogsMenuItem(),
				this.createDownloadWebLogsMenuItem(),
				this.createAuditLogsMenuItem(),
				this.createClientLogsMenuItem(),

			],
		});

		return _item;
	}

	protected createClientLogsMenuItem(): MenuItem<INavItem> {
		const _item: MenuItem = this.createItem({
			id: 'clientlogs',
			name: 'View Client Logs',
			onSelect: this.downloadClientLogs.bind(this)
		});

		this.hideByGlobalPermission(_item, GlobalPermissions.PERMISSIONS_FOR_LOGS_DOWNLOAD);

		return _item;
	}

	protected downloadClientLogs(): void {
		this.overlayService.create(ClientLogOverlayComponent).pipe(
			this.takeOneUntilDestroyed()
		).subscribe();
	}

	protected createAuditLogsMenuItem(): MenuItem<INavItem> {
		const _item: MenuItem = this.createItem({
			id: 'auditlogs',
			name: 'View Audit Logs',
			url: '/audit-logs',
		});

		this.hideByGlobalPermission(_item, GlobalPermissions.PERMISSIONS_FOR_LOGS_DOWNLOAD);

		return _item;
	}

	protected createDownloadAllServerLogsMenuItem(): MenuItem<INavItem> {
		const _item: MenuItem = this.createItem({
			id: 'downloadallserverlogs',
			name: 'Download All Server Logs',
			onSelect: this.downloadAllServerLogs.bind(this)
		});

		this.hideByGlobalPermission(_item, GlobalPermissions.PERMISSIONS_FOR_LOGS_DOWNLOAD);

		return _item;
	}

	protected createDownloadServerErrorLogsMenuItem(): MenuItem<INavItem> {
		const _item: MenuItem = this.createItem({
			id: 'downloadservererrorlogs',
			name: 'Download Server Error Logs',
			onSelect: this.downloadServerErrorLogs.bind(this)
		});

		this.hideByGlobalPermission(_item, GlobalPermissions.PERMISSIONS_FOR_LOGS_DOWNLOAD);

		return _item;
	}

	protected createClearWebLogsMenuItem(): MenuItem<INavItem> {
		return this.createItem({
			id: 'clearweblogs',
			name: 'Clear Web Browser Logs',
			onSelect: this.clearWebLogs.bind(this)
		});
	}

	protected createDownloadWebLogsMenuItem(): MenuItem<INavItem> {
		return this.createItem({
			id: 'downloadweblogs',
			name: 'Download Web Browser Logs',
			onSelect: this.downloadWebLogs.bind(this),
		});
	}

	protected createLicensingMenuItem(): MenuItem<INavItem> {
		const _item: MenuItem<INavItem> = this.createItem({
			id: 'licensing',
			name: 'Product Licensing',
			url: '/licensing',
			isVisible: new BehaviorSubject(false),
		});

		this.hideByGlobalPermission(_item, GlobalPermissions.PERMISSIONS_FOR_VIEW_LICENSE_STATE);

		return _item;
	}

	protected createSettingsMenuItem(): MenuItem<INavItem> {
		return this.createItem({
			id: 'settings',
			name: 'Settings',
			children: SETTINGS_NAVIGATION.sections.reduce((_flattened: MenuItem<INavItem>[], _section: INavSection) => {
				_flattened.push(..._section.items.map((_item: INavItem) => {
					if (_item.display === 'About Adaptiva Server')
						_item.display = `About ${this.systemConfig.appDisplayName.value} Server`;

					return this.createSideNavItem(_item);
				}));

				return _flattened;
			}, [])
		});
	}

	protected createThemesMenuItem(): MenuItem<INavItem> {
		const _item: MenuItem<INavItem> = this.createItem({
			id: 'themes',
			name: 'Themes',
			url: '/themes',
			isVisible: new BehaviorSubject(false),
		});

		this.enableThemesMenuItem(_item);

		return _item;
	}

	protected enableThemesMenuItem(_item: MenuItem<INavItem>): void {
		this.objectSecurity.isAnyObjectVisibleForClass(EJavaClassNames.Theme).pipe(
			this.takeOneUntilDestroyed()
		).subscribe({
			next: (_result: boolean) => _item.isVisible.next(_result),
		});
	}

	protected createImportMenuItem(): MenuItem<INavItem> {
		const _item: MenuItem<INavItem> = this.createItem({
			id: 'import',
			name: 'Import',
			onSelect: this.openExim.bind(this),
			isVisible: new BehaviorSubject(false),
		});

		this.hideByGlobalPermission(_item, GlobalPermissions.PERMISSIONS_FOR_OBJECT_IMPORTING);

		return _item;
	}

	protected openExim(): void {
		this.overlayService.load({
			overlayId: 'ObjectTransferActionOverlayComponent'
		}).pipe(take(1)).subscribe();
	}

	protected clearWebLogs(): void {
		this.overlayService.confirm(
			'Are You Sure?',
			'Clearing UI logs cannot be undone. Are you sure you want to continue?',
		).pipe(take(1)).subscribe({
			next: (_confirmed: boolean) => {
				if (!_confirmed)
					return;

				this.logger.clear();
				this.snackbarService.display('Successfully cleared UI logs');
			}
		});
	}

	protected downloadWebLogs(): void {
		this.logger.export();
	}

	protected downloadAllServerLogs(): void {
		this.snackbarService.display({
			message: 'Fetching All Server Logs',
			dismissable: true,
			duration: -1
		});

		this.serverLogsService.getAllServerLogs().pipe(
			take(1),
			map((_objectBlob: Blob) => FileGenerator.generate({
				content: [_objectBlob],
				extension: 'zip',
				fileType: 'application/octet',
				name: 'logs'
			}))
		).subscribe({
			next: () => {
				this.snackbarService.closeSnackbar();
			},
			error: (_err: Error) => {
				this.snackbarService.closeSnackbar();
				this.logger.error(
					'HeaderComponent.downloadAllServerLogs',
					'Failed to get all server logs',
					_err
				)();
			}
		});
	}

	protected downloadServerErrorLogs(): void {
		this.serverLogsService.getServerErrorLogs().pipe(
			map((_objectBlob: Blob) => FileGenerator.generate({
				content: [_objectBlob],
				extension: 'err',
				fileType: 'application/octet',
				name: 'adaptiva'
			})),
			take(1)
		).subscribe({
			error: (_err: Error) => {
				this.logger.error(
					'HeaderComponent.downloadServerErrorLogs',
					'Failed to get server error logs',
					_err
				)();
			}
		});
	}

	protected hideByGlobalPermission(_item: MenuItem, _permission: byte): void {
		this.globalUiPermissions.checkPermission(_permission).pipe(
			this.takeOneUntilDestroyed()
		).subscribe({
			next: (_hasPermissions: boolean) => _item.isVisible.next(_hasPermissions)
		});
	}

}
